import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (res.ok) {
      navigate("/", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-slate-50">
      {/* Left visual panel */}
      <div className="hidden lg:block relative overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODE3OTIxNzJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white text-slate-900 flex items-center justify-center font-display font-black text-lg rounded-sm">
              P
            </div>
            <span className="font-display font-bold tracking-tight">PARTIDO · GESTIÓN</span>
          </div>
          <div>
            <h2 className="font-display text-4xl xl:text-5xl font-black leading-tight tracking-tight">
              Base de datos de contactos
            </h2>
            <p className="mt-4 text-slate-300 max-w-md text-base">
              Plataforma institucional para la gestión de afiliados, apoderados y
              consentimientos de comunicación del partido en Terrassa.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat label="Distritos" value="7" />
              <Stat label="Barrios" value="40+" />
              <Stat label="GDPR" value="OK" />
            </div>
          </div>
          <p className="text-xs text-slate-400 tracking-wider uppercase">
            Acceso restringido · Solo personal autorizado
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Acceso
            </p>
            <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-slate-950">
              Inicia sesión
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Introduce tus credenciales para entrar al panel.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Tu nombre de usuario"
                required
                className="rounded-sm border-slate-300 focus-visible:ring-1 focus-visible:ring-slate-900 focus-visible:ring-offset-0"
                data-testid="login-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm border-slate-300 focus-visible:ring-1 focus-visible:ring-slate-900 focus-visible:ring-offset-0"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="bg-red-50 border-l-2 border-red-600 px-3 py-2 text-sm text-red-700"
                data-testid="login-error-message"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 transition-colors duration-200 h-11 font-semibold tracking-wide"
              data-testid="login-submit-button"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Entrar
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="font-display text-3xl font-black">{value}</p>
    <p className="text-xs uppercase tracking-wider text-slate-400 mt-1">{label}</p>
  </div>
);
